import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mindMapApi, projectsApi, tasksApi } from '../api/client';
import { useMindMapStore, useUIStore } from '../store';
import type { MindMapNode, Task, Project } from '../types';
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Plus,
  Minus,
  Move,
  MousePointer2,
  Save,
} from 'lucide-react';

// 画布尺寸（足够大的幕布范围）
const CANVAS_WIDTH = 5000;
const CANVAS_HEIGHT = 5000;

// 节点尺寸
const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;

// 树形布局参数
const LEVEL_HEIGHT = 120;
const SIBLING_GAP = 20;

interface TreeNode extends MindMapNode {
  children: TreeNode[];
  x: number;
  y: number;
  width: number;
  height: number;
}

export function MindMapView() {
  const queryClient = useQueryClient();
  const { selectedProjectId, openTaskModal, setSelectedTaskId } = useUIStore();
  const {
    scale,
    translateX,
    translateY,
    isDragging,
    draggedNodeId,
    setScale,
    setTranslate,
    setIsDragging,
    setDraggedNodeId,
    resetView,
    zoomIn,
    zoomOut,
  } = useMindMapStore();

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // 获取项目列表
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getAll(),
  });

  // 获取思维导图节点
  const { data: nodesData, isLoading } = useQuery({
    queryKey: ['mindmap', selectedProjectId],
    queryFn: () =>
      selectedProjectId
        ? mindMapApi.getByProject(selectedProjectId)
        : Promise.resolve({ success: true, data: [] }),
    enabled: !!selectedProjectId,
  });

  // 获取任务列表
  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.getAll(),
  });

  const projects = projectsData?.success ? projectsData.data : [];
  const nodes = nodesData?.success ? nodesData.data : [];
  const tasks = tasksData?.success ? tasksData.data : [];

  // 更新节点位置
  const updatePositionMutation = useMutation({
    mutationFn: (params: { id: string; x: number; y: number }) =>
      mindMapApi.update(params.id, { x: params.x, y: params.y }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmap', selectedProjectId] });
    },
  });

  // 构建树形结构
  const treeData = useMemo(() => {
    if (!selectedProjectId || nodes.length === 0) {
      // 如果没有选择项目，创建一个默认的根节点
      if (projects.length > 0) {
        return null;
      }
      return null;
    }

    // 找到根节点
    const rootNode = nodes.find((n) => n.type === 'root');
    if (!rootNode) return null;

    // 构建树
    const buildTree = (parentId: string | null): TreeNode[] => {
      const children = nodes.filter((n) => n.parentId === parentId);
      return children.map((child) => ({
        ...child,
        children: buildTree(child.id),
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      }));
    };

    const tree: TreeNode = {
      ...rootNode,
      children: buildTree(rootNode.id),
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    };

    // 计算布局位置
    const calculateLayout = (node: TreeNode, x: number, y: number): void => {
      node.x = x;
      node.y = y;

      if (node.children.length > 0) {
        const totalHeight =
          node.children.length * NODE_HEIGHT +
          (node.children.length - 1) * SIBLING_GAP;
        let startY = y - totalHeight / 2 + NODE_HEIGHT / 2;

        node.children.forEach((child) => {
          calculateLayout(child, x + NODE_WIDTH + LEVEL_HEIGHT, startY);
          startY += NODE_HEIGHT + SIBLING_GAP;
        });
      }
    };

    calculateLayout(tree, CANVAS_WIDTH / 2 - NODE_WIDTH / 2, CANVAS_HEIGHT / 2);

    return tree;
  }, [nodes, projects.length, selectedProjectId]);

  // 获取所有节点（扁平化）
  const allNodes = useMemo(() => {
    const result: TreeNode[] = [];
    const flatten = (node: TreeNode) => {
      result.push(node);
      node.children.forEach(flatten);
    };
    if (treeData) flatten(treeData);
    return result;
  }, [treeData]);

  // 获取连接线
  const connections = useMemo(() => {
    const result: { from: TreeNode; to: TreeNode }[] = [];
    const findConnections = (node: TreeNode) => {
      node.children.forEach((child) => {
        result.push({ from: node, to: child });
        findConnections(child);
      });
    };
    if (treeData) findConnections(treeData);
    return result;
  }, [treeData]);

  // 获取节点颜色
  const getNodeColor = (node: TreeNode) => {
    if (node.type === 'root') return '#3B82F6';
    if (node.type === 'milestone') return '#8B5CF6';
    const project = projects.find((p) => p.id === selectedProjectId);
    return project?.color || '#6B7280';
  };

  // 获取关联的任务
  const getTask = (taskId: string | null) => {
    if (!taskId) return null;
    return tasks.find((t) => t.id === taskId);
  };

  // 处理鼠标滚轮缩放
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setScale(scale * delta);
    },
    [scale, setScale]
  );

  // 处理画布拖拽开始
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === svgRef.current || (e.target as HTMLElement).tagName === 'svg') {
        setIsPanning(true);
        setPanStart({ x: e.clientX - translateX, y: e.clientY - translateY });
      }
    },
    [translateX, translateY]
  );

  // 处理节点拖拽开始
  const handleNodeMouseDown = useCallback(
    (e: React.MouseEvent, node: TreeNode) => {
      e.stopPropagation();
      setDraggedNodeId(node.id);
      setDragOffset({
        x: e.clientX / scale - node.x,
        y: e.clientY / scale - node.y,
      });
    },
    [scale, setDraggedNodeId]
  );

  // 处理鼠标移动
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isPanning) {
        setTranslate(e.clientX - panStart.x, e.clientY - panStart.y);
      } else if (draggedNodeId && treeData) {
        const newX = e.clientX / scale - dragOffset.x;
        const newY = e.clientY / scale - dragOffset.y;
        updatePositionMutation.mutate({ id: draggedNodeId, x: newX, y: newY });
      }
    },
    [
      isPanning,
      draggedNodeId,
      panStart,
      dragOffset,
      scale,
      setTranslate,
      treeData,
      updatePositionMutation,
    ]
  );

  // 处理鼠标释放
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setDraggedNodeId(null);
  }, [setDraggedNodeId]);

  // 处理节点点击
  const handleNodeClick = useCallback(
    (node: TreeNode) => {
      if (node.taskId) {
        setSelectedTaskId(node.taskId);
        openTaskModal(node.taskId);
      }
    },
    [openTaskModal, setSelectedTaskId]
  );

  // 初始化中心位置
  useEffect(() => {
    if (containerRef.current && translateX === 0 && translateY === 0) {
      const rect = containerRef.current.getBoundingClientRect();
      setTranslate(
        rect.width / 2 - CANVAS_WIDTH / 2,
        rect.height / 2 - CANVAS_HEIGHT / 2
      );
    }
  }, [setTranslate, translateX, translateY]);

  if (!selectedProjectId) {
    return (
      <div className="flex flex-col items-center justify-center h-[600px] bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 7m0 13V7" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">选择项目</h3>
        <p className="text-gray-500 mb-6">请先在项目视图中选择一个项目</p>
        <div className="flex gap-2">
          {projects.slice(0, 5).map((project) => (
            <button
              key={project.id}
              onClick={() => useUIStore.getState().setSelectedProjectId(project.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
            >
              {project.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 工具栏 */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {projects.find((p) => p.id === selectedProjectId)?.name} - 思维导图
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Move className="w-4 h-4" />
            <span>拖拽画布移动</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="缩小"
          >
            <Minus className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="放大"
          >
            <Plus className="w-5 h-5" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-2" />
          <button
            onClick={resetView}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="重置视图"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-sm">重置</span>
          </button>
        </div>
      </div>

      {/* 画布容器 */}
      <div
        ref={containerRef}
        className="relative h-[600px] bg-gray-50 rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* SVG画布 */}
        <svg
          ref={svgRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{
            transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
          className="absolute top-0 left-0"
        >
          {/* 背景网格 */}
          <defs>
            <pattern
              id="grid"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 50 0 L 0 0 0 50"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill="url(#grid)" />

          {/* 连接线 */}
          {connections.map((conn, index) => (
            <g key={index}>
              <path
                d={`M ${conn.from.x + NODE_WIDTH} ${conn.from.y + NODE_HEIGHT / 2} 
                    C ${conn.from.x + NODE_WIDTH + LEVEL_HEIGHT / 2} ${conn.from.y + NODE_HEIGHT / 2}
                      ${conn.to.x - LEVEL_HEIGHT / 2} ${conn.to.y + NODE_HEIGHT / 2}
                      ${conn.to.x} ${conn.to.y + NODE_HEIGHT / 2}`}
                fill="none"
                stroke="#D1D5DB"
                strokeWidth="2"
              />
            </g>
          ))}

          {/* 节点 */}
          {allNodes.map((node) => {
            const task = getTask(node.taskId);
            const isDraggingNode = draggedNodeId === node.id;

            return (
              <g
                key={node.id}
                transform={`translate(${node.x}, ${node.y})`}
                onMouseDown={(e) => handleNodeMouseDown(e as any, node)}
                onClick={() => handleNodeClick(node)}
                className="cursor-pointer"
                style={{ cursor: isDraggingNode ? 'grabbing' : 'pointer' }}
              >
                {/* 节点背景 */}
                <rect
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx="8"
                  fill="white"
                  stroke={getNodeColor(node)}
                  strokeWidth={node.type === 'root' ? 3 : 2}
                  filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                />

                {/* 节点内容 */}
                <foreignObject
                  x="8"
                  y="8"
                  width={NODE_WIDTH - 16}
                  height={NODE_HEIGHT - 16}
                >
                  <div className="flex flex-col h-full justify-center">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {node.label}
                    </p>
                    {task && (
                      <p className="text-xs text-gray-500 truncate">
                        {task.status === 'completed'
                          ? '✓ 已完成'
                          : task.status === 'in_progress'
                          ? '◐ 进行中'
                          : '○ 待办'}
                      </p>
                    )}
                  </div>
                </foreignObject>

                {/* 展开/折叠指示器 */}
                {node.children.length > 0 && (
                  <circle
                    cx={NODE_WIDTH}
                    cy={NODE_HEIGHT / 2}
                    r="8"
                    fill="white"
                    stroke={getNodeColor(node)}
                    strokeWidth="2"
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* 空状态 */}
        {allNodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.447-.894L15 7m0 13V7" />
                </svg>
              </div>
              <p className="text-gray-500">暂无思维导图数据</p>
              <p className="text-sm text-gray-400 mt-1">请先创建任务</p>
            </div>
          </div>
        )}
      </div>

      {/* 图例 */}
      <div className="flex items-center gap-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <span className="text-sm font-medium text-gray-700">图例：</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500" />
          <span className="text-sm text-gray-600">项目根节点</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-500" />
          <span className="text-sm text-gray-600">里程碑</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-400" />
          <span className="text-sm text-gray-600">任务</span>
        </div>
      </div>
    </div>
  );
}
